/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView, useTrackSearch } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, LogOut, Wheat, Trash2, Search, Calculator,
} from "lucide-react";
import RationCalculator from "@/components/nutrition/RationCalculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FEED_TYPES = ["concentre", "fourrage", "foin", "paille", "ensilage", "mineraux", "autre"];

export default function Nutrition() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [tab, setTab] = useState("stocks");
  const [stocksData, setStocksData] = useState({ items: [], stats: {} });
  const [feedingsData, setFeedingsData] = useState({ feedings: [], animals: [], stats: {} });
  const [allAnimals, setAllAnimals] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState("");
  const [openStockDialog, setOpenStockDialog] = useState(false);
  const [openFeedingDialog, setOpenFeedingDialog] = useState(false);

  useTrackModuleView("nutrition");
  useTrackSearch("nutrition", search);

  const fetchAll = async () => {
    setLoadingData(true);
    try {
      const [s, f, a] = await Promise.all([
        axios.get(`${API}/nutrition/stocks`, { withCredentials: true }),
        axios.get(`${API}/nutrition/feedings`, { withCredentials: true }),
        axios.get(`${API}/animals`, { withCredentials: true }),
      ]);
      setStocksData(s.data);
      setFeedingsData(f.data);
      setAllAnimals(a.data.animals || []);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") fetchAll();
  }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  const deleteStock = async (id) => {
    try {
      await axios.delete(`${API}/nutrition/stocks/${id}`, { withCredentials: true });
      toast.success(t.app.nutrition.deleted_toast);
      fetchAll();
    } catch (e) { toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) }); }
  };
  const deleteFeeding = async (id) => {
    try {
      await axios.delete(`${API}/nutrition/feedings/${id}`, { withCredentials: true });
      toast.success(t.app.nutrition.deleted_toast);
      fetchAll();
    } catch (e) { toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) }); }
  };

  const animalsById = {};
  [...allAnimals, ...(feedingsData.animals || [])].forEach((a) => { if (a) animalsById[a.id] = a; });

  const stocksFiltered = stocksData.items.filter((s) => {
    const q = search.toLowerCase();
    return !q || s.name?.toLowerCase().includes(q) || s.supplier?.toLowerCase().includes(q);
  });
  const feedingsFiltered = feedingsData.feedings.filter((f) => {
    const q = search.toLowerCase();
    const an = animalsById[f.animal_id];
    return !q || f.feed_name?.toLowerCase().includes(q) || an?.tag?.toLowerCase().includes(q) || an?.name?.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button onClick={logout} variant="ghost" size="sm" className="text-white/80">
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <Wheat size={14} /> {t.app.nutrition.overline}
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.nutrition.title}</h1>
            <p className="mt-1 text-[#5C6B5C]">{t.app.nutrition.subtitle}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={openStockDialog} onOpenChange={setOpenStockDialog}>
              <DialogTrigger asChild>
                <Button data-testid="open-create-stock" variant="outline" className="h-12 px-5 rounded-xl border-[#1C3A27] text-[#1C3A27] hover:bg-[#1C3A27] hover:text-white font-semibold">
                  <Plus size={18} className="me-2" /> {t.app.nutrition.new_stock}
                </Button>
              </DialogTrigger>
              <StockDialog onClose={() => setOpenStockDialog(false)} onSaved={() => { setOpenStockDialog(false); fetchAll(); }} />
            </Dialog>
            {allAnimals.length > 0 && (
              <Dialog open={openFeedingDialog} onOpenChange={setOpenFeedingDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="open-create-feeding" className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold">
                    <Plus size={18} className="me-2" /> {t.app.nutrition.new_feeding}
                  </Button>
                </DialogTrigger>
                <FeedingDialog
                  stocks={stocksData.items}
                  animals={allAnimals}
                  onClose={() => setOpenFeedingDialog(false)}
                  onSaved={() => { setOpenFeedingDialog(false); fetchAll(); }}
                />
              </Dialog>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard label={t.app.nutrition.stat_total_stocks} value={stocksData.stats?.total_items || 0} />
          <StatCard label={t.app.nutrition.stat_stock_value} value={`${(stocksData.stats?.total_value_tnd || 0).toFixed(0)} TND`} accent="#1E6BBA" />
          <StatCard label={t.app.nutrition.stat_total_feedings} value={feedingsData.stats?.total_feedings || 0} accent="#1C7A3C" />
          <StatCard label={t.app.nutrition.stat_feed_cost} value={`${(feedingsData.stats?.total_cost_tnd || 0).toFixed(0)} TND`} accent="#C84B31" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-[#E2E4DF]">
          <TabBtn active={tab === "stocks"} onClick={() => setTab("stocks")} testid="tab-stocks">{t.app.nutrition.tab_stocks}</TabBtn>
          <TabBtn active={tab === "feedings"} onClick={() => setTab("feedings")} testid="tab-feedings">{t.app.nutrition.tab_feedings}</TabBtn>
          <TabBtn active={tab === "calculator"} onClick={() => setTab("calculator")} testid="tab-calculator">
            <span className="inline-flex items-center gap-1.5"><Calculator size={14} /> {t.app.nutrition.tab_calculator || "Calculateur"}</span>
          </TabBtn>
        </div>

        {tab === "calculator" ? (
          <RationCalculator />
        ) : (
        <>
        <div className="relative max-w-md mb-5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
          <Input data-testid="nutrition-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.app.nutrition.search_placeholder} className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl" />
        </div>

        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {loadingData ? (
            <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
          ) : tab === "stocks" ? (
            stocksFiltered.length === 0 ? (
              <div className="p-12 text-center text-[#5C6B5C]">
                <Wheat size={40} className="mx-auto opacity-40 mb-3" />
                {t.app.nutrition.stocks_empty}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_name}</th>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_type}</th>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_quantity}</th>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_cost_per_kg}</th>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_value}</th>
                      <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_supplier}</th>
                      <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocksFiltered.map((s) => (
                      <tr key={s.id} data-testid={`stock-row-${s.id}`} className="border-t border-[#E2E4DF]">
                        <td className="px-5 py-4 font-medium text-[#1C3A27]">{s.name}</td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{t.app.nutrition.types[s.feed_type]}</td>
                        <td className="px-5 py-4 text-[#1C3A27] font-semibold">{(s.quantity_kg || 0).toFixed(1)} kg</td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{(s.cost_per_kg || 0).toFixed(2)} TND</td>
                        <td className="px-5 py-4 text-[#1C3A27] font-semibold">{((s.quantity_kg || 0) * (s.cost_per_kg || 0)).toFixed(2)} TND</td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{s.supplier || "—"}</td>
                        <td className="px-5 py-4 text-end">
                          <button onClick={() => { if (window.confirm(t.app.nutrition.delete_stock_title)) deleteStock(s.id); }} data-testid={`delete-stock-${s.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : feedingsFiltered.length === 0 ? (
            <div className="p-12 text-center text-[#5C6B5C]">
              {allAnimals.length === 0 ? t.app.nutrition.add_animal_first : t.app.nutrition.feedings_empty}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_date}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_animal}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_feed}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_qty}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.nutrition.th_cost}</th>
                    <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedingsFiltered.map((f) => {
                    const an = animalsById[f.animal_id];
                    return (
                      <tr key={f.id} data-testid={`feeding-row-${f.id}`} className="border-t border-[#E2E4DF]">
                        <td className="px-5 py-4 text-[#1C3A27] font-medium">{f.date}</td>
                        <td className="px-5 py-4 text-[#1C3A27]">
                          {an ? <span className="font-mono font-semibold">{an.tag}</span> : "—"}
                          {an?.name && <span className="text-xs text-[#5C6B5C]"> · {an.name}</span>}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{f.feed_name || "—"}</td>
                        <td className="px-5 py-4 text-[#1C3A27] font-semibold">{(f.quantity_kg || 0).toFixed(1)} kg</td>
                        <td className="px-5 py-4 text-[#1C3A27] font-semibold">{f.cost != null ? `${f.cost.toFixed(2)} TND` : "—"}</td>
                        <td className="px-5 py-4 text-end">
                          <button onClick={() => { if (window.confirm(t.app.nutrition.delete_feeding_title)) deleteFeeding(f.id); }} data-testid={`delete-feeding-${f.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} type="button"
      className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${active ? "border-[#E05A3D] text-[#E05A3D]" : "border-transparent text-[#5C6B5C] hover:text-[#1C3A27]"}`}>
      {children}
    </button>
  );
}

function StatCard({ label, value, accent = "#1C3A27" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-5">
      <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold truncate">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function StockDialog({ onClose, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", feed_type: "concentre", quantity_kg: "", cost_per_kg: "", supplier: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/nutrition/stocks`, {
        name: form.name.trim(), feed_type: form.feed_type,
        quantity_kg: parseFloat(form.quantity_kg), cost_per_kg: parseFloat(form.cost_per_kg),
        supplier: form.supplier?.trim() || undefined, notes: form.notes?.trim() || undefined,
      }, { withCredentials: true });
      toast.success(t.app.nutrition.saved_toast);
      onSaved?.();
    } catch (err) { setError(formatApiError(err?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  return (
    <DialogContent className="max-w-lg rounded-2xl">
      <DialogHeader><DialogTitle className="font-display text-2xl">{t.app.nutrition.add_stock_title}</DialogTitle></DialogHeader>
      <form onSubmit={submit} data-testid="stock-form" className="grid grid-cols-2 gap-4 mt-2">
        <div className="col-span-2">
          <Label>{t.app.nutrition.name_label}</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.app.nutrition.name_ph} className="mt-1.5 h-11 bg-white" data-testid="stock-name" />
        </div>
        <div>
          <Label>{t.app.nutrition.type_label}</Label>
          <Select value={form.feed_type} onValueChange={(v) => setForm({ ...form, feed_type: v })}>
            <SelectTrigger className="mt-1.5 h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{FEED_TYPES.map((ft) => <SelectItem key={ft} value={ft}>{t.app.nutrition.types[ft]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.nutrition.quantity_label}</Label>
          <Input required type="number" min={0} step={0.1} value={form.quantity_kg} onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })} className="mt-1.5 h-11 bg-white" />
        </div>
        <div className="col-span-2">
          <Label>{t.app.nutrition.cost_per_kg_label}</Label>
          <Input required type="number" min={0} step={0.01} value={form.cost_per_kg} onChange={(e) => setForm({ ...form, cost_per_kg: e.target.value })} className="mt-1.5 h-11 bg-white" />
        </div>
        <div className="col-span-2">
          <Label>{t.app.nutrition.supplier_label}</Label>
          <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder={t.app.nutrition.supplier_ph} className="mt-1.5 h-11 bg-white" />
        </div>
        {error && <div className="col-span-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
        <DialogFooter className="col-span-2">
          <Button type="button" variant="outline" onClick={onClose}>{t.app.common.cancel}</Button>
          <Button type="submit" disabled={submitting} data-testid="submit-stock" className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
            {submitting ? t.app.common.saving : t.app.common.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function FeedingDialog({ stocks, animals, onClose, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    animal_id: animals[0]?.id || "", feed_item_id: "", feed_name: "",
    date: new Date().toISOString().slice(0, 10), quantity_kg: "", cost: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/nutrition/feedings`, {
        animal_id: form.animal_id,
        feed_item_id: form.feed_item_id || undefined,
        feed_name: form.feed_name?.trim() || undefined,
        date: form.date,
        quantity_kg: parseFloat(form.quantity_kg),
        cost: form.cost === "" ? undefined : parseFloat(form.cost),
        notes: form.notes?.trim() || undefined,
      }, { withCredentials: true });
      toast.success(t.app.nutrition.saved_toast);
      onSaved?.();
    } catch (err) { setError(formatApiError(err?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  return (
    <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display text-2xl">{t.app.nutrition.add_feeding_title}</DialogTitle></DialogHeader>
      <form onSubmit={submit} data-testid="feeding-form" className="grid grid-cols-2 gap-4 mt-2">
        <div className="col-span-2">
          <Label>{t.app.nutrition.animal_label}</Label>
          <Select value={form.animal_id} onValueChange={(v) => setForm({ ...form, animal_id: v })}>
            <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="feeding-animal-select"><SelectValue placeholder={t.app.nutrition.animal_select_ph} /></SelectTrigger>
            <SelectContent>{animals.map((a) => <SelectItem key={a.id} value={a.id}>{a.tag}{a.name ? ` — ${a.name}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label>{t.app.nutrition.feed_item_label} <span className="text-[#5C6B5C] text-xs">{t.app.nutrition.feed_item_optional}</span></Label>
          <Select value={form.feed_item_id || "_none_"} onValueChange={(v) => setForm({ ...form, feed_item_id: v === "_none_" ? "" : v })}>
            <SelectTrigger className="mt-1.5 h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none_">{t.app.nutrition.feed_item_none}</SelectItem>
              {stocks.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({(s.quantity_kg || 0).toFixed(1)} kg)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!form.feed_item_id && (
          <div className="col-span-2">
            <Label>{t.app.nutrition.feed_name_label}</Label>
            <Input value={form.feed_name} onChange={(e) => setForm({ ...form, feed_name: e.target.value })} placeholder={t.app.nutrition.feed_name_ph} className="mt-1.5 h-11 bg-white" />
          </div>
        )}
        <div>
          <Label>{t.app.nutrition.date_label}</Label>
          <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11 bg-white" />
        </div>
        <div>
          <Label>{t.app.nutrition.qty_label}</Label>
          <Input required type="number" min={0} step={0.1} value={form.quantity_kg} onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })} className="mt-1.5 h-11 bg-white" />
        </div>
        <div className="col-span-2">
          <Label>{t.app.nutrition.cost_label_override}</Label>
          <Input type="number" min={0} step={0.01} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-1.5 h-11 bg-white" />
          <p className="text-xs text-[#5C6B5C] mt-1">{t.app.nutrition.cost_hint}</p>
        </div>
        <div className="col-span-2">
          <Label>{t.app.nutrition.notes_label}</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t.app.nutrition.notes_ph} className="mt-1.5 bg-white resize-none" />
        </div>
        {error && <div className="col-span-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
        <DialogFooter className="col-span-2">
          <Button type="button" variant="outline" onClick={onClose}>{t.app.common.cancel}</Button>
          <Button type="submit" disabled={submitting} data-testid="submit-feeding" className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
            {submitting ? t.app.common.saving : t.app.common.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
